<template>
  <v-container>
    <div>
      <v-row
        align="center"
        class="mb-4"
        justify="space-between"
      >
        <v-col cols="auto">
          <v-card
            v-if="screen && screen.hasMultipleStaff"
            flat
          >
            <v-card-text>
              <v-select
                v-model="selectedStaffMember"
                :items="screen.staffOptions"
                item-value="value"
                item-text="label"
                style="min-width: 200px"
                dense
                outlined
                hide-details
                :label="$t($ts.selectStaffMember)"
                class="header-select"
                @change="refreshStats"
              >
                <template #prepend>
                  <v-icon left>
                    mdi-account-supervisor-circle
                  </v-icon>
                </template>
              </v-select>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="auto">
          <v-card flat>
            <v-card-text class="d-flex">
              <v-select
                v-model="selectedYear"
                :items="yearOptions"
                item-value="value"
                item-text="label"
                style="min-width: 150px"
                dense
                outlined
                hide-details
                class="header-select mr-4"
                @change="refreshStats"
              >
                <template #prepend>
                  <v-icon left>
                    mdi-calendar
                  </v-icon>
                </template>
              </v-select>

              <v-select
                v-model="selectedMonth"
                :items="monthOptions"
                item-value="value"
                item-text="label"
                style="min-width: 150px"
                dense
                outlined
                hide-details
                class="header-select"
                @change="refreshStats"
              >
                <template #prepend>
                  <v-icon left>
                    mdi-calendar-month
                  </v-icon>
                </template>
              </v-select>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card flat>
        <v-tabs
          v-model="activeTab"
          background-color="#866afe"
          dark
        >
          <v-tab>{{ $t($ts.earningsTrend) }}</v-tab>
          <v-tab>{{ $t($ts.statistics.earnings) }}</v-tab>
          <v-tab>{{ $t($ts.servicesBooked) }}</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- Overview Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-skeleton-loader
                  v-if="loading || !screen"
                  type="image"
                  class="mx-auto"
                />
                <template v-else>
                  <commit-chart
                    v-if="screen.month == 0"
                    :current-year="seriesValues('current')"
                    :previous-year="seriesValues('previous')"
                    :year="screen.year"
                  />
                  <daily-commit-chart
                    v-else
                    :data="seriesValues('earnings')"
                    :year="screen.year"
                    :month="screen.month"
                    :month-name="screen.title"
                  />
                </template>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Details Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-radio-group
                  v-model="displayMode"
                  row
                  dense
                  class="mt-0 pt-0 mr-4"
                >
                  <v-radio
                    :label="$t($ts.statistics.earnings)"
                    value="earnings"
                  />
                  <v-radio
                    :label="$t($ts.statistics.appointments)"
                    value="appointments"
                  />
                </v-radio-group>
                <v-switch
                  v-if="screen && screen.hasMultipleStaff"
                  v-model="showPerStaffMember"
                  :label="$t($ts.statistics.showPerStaffMember)"
                  class="mt-0 pt-0"
                  dense
                  color="#866afe"
                />

                <v-data-table
                  :headers="headersFor(detailsTable)"
                  :items="itemsFor(detailsTable)"
                  :loading="loading"
                  :items-per-page="-1"
                  disable-sort
                  hide-default-footer
                  class="mb-6 custom-table horizontal-scroll"
                  dense
                >
                  <template #loading>
                    <v-skeleton-loader
                      type="table-row-divider@6"
                      class="mx-auto"
                    />
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- Services Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-data-table
                  :headers="headersFor(servicesTable)"
                  :items="itemsFor(servicesTable)"
                  :loading="loading"
                  disable-sort
                >
                  <template #loading>
                    <v-skeleton-loader
                      type="table-row-divider@6"
                      class="mx-auto"
                    />
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>
  </v-container>
</template>

<script lang="ts">
import { Component, Watch } from "vue-property-decorator";
import CommitChart from "../../components/global/CommitChart.vue";
import DailyCommitChart from "@/components/global/DailyCommitChart.vue";
import Calendar from "@/abstract-classes/Calendar";
import { IScreenOptionDTO, IScreenTableDTO, IStatisticsScreenDTO } from "@shared/types";
import { statisticsModule } from "@/store/modules/statistics/statisticsModule";

/**
 * Renders the statistics screen exactly as the server returns it: labels, amounts, tables and
 * chart series all come ready from GET Statistics/current/screen.
 */
@Component({
  name: "statistics",
  components: { CommitChart, DailyCommitChart },
})
export default class Statistics extends Calendar {
  displayMode: string = "earnings";
  activeTab: number = 0;
  showPerStaffMember: boolean = false;
  selectedStaffMember: number = 0;
  selectedYear: number = new Date().getFullYear();
  selectedMonth: number = 0;
  loading: boolean = false;
  screen: IStatisticsScreenDTO | null = null;

  public readonly STORAGE_KEY = "statistics_settings";

  get yearOptions(): IScreenOptionDTO[] {
    return this.screen?.yearOptions ?? [{ value: this.selectedYear, label: String(this.selectedYear) }];
  }

  get monthOptions(): IScreenOptionDTO[] {
    return this.screen?.monthOptions ?? [{ value: this.selectedMonth, label: "" }];
  }

  get detailsTable(): IScreenTableDTO | null {
    if (!this.screen) return null;
    if (this.showPerStaffMember && this.screen.hasMultipleStaff) {
      return this.displayMode === "earnings" ? this.screen.staffEarningsTable : this.screen.staffAppointmentsTable;
    }
    return this.displayMode === "earnings" ? this.screen.earningsTable : this.screen.appointmentsTable;
  }

  get servicesTable(): IScreenTableDTO | null {
    return this.screen?.servicesTable ?? null;
  }

  seriesValues(key: string): number[] {
    return this.screen?.chart.series.find((s) => s.key === key)?.values ?? [];
  }

  headersFor(table: IScreenTableDTO | null): { text: string; value: string }[] {
    return table ? table.columns.map((c) => ({ text: c.label, value: c.key })) : [];
  }

  itemsFor(table: IScreenTableDTO | null): Record<string, string>[] {
    if (!table) return [];
    return table.rows.map((row) => {
      const item: Record<string, string> = {};
      table.columns.forEach((column, i) => (item[column.key] = row.cells[i]?.text ?? ""));
      return item;
    });
  }

  public saveSettings() {
    const settings = {
      selectedStaffMember: this.selectedStaffMember,
      selectedYear: this.selectedYear,
      selectedMonth: this.selectedMonth,
      activeTab: this.activeTab,
      displayMode: this.displayMode,
      showPerStaffMember: this.showPerStaffMember,
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
  }

  public loadSettings() {
    const savedSettings = localStorage.getItem(this.STORAGE_KEY);
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      this.selectedStaffMember = settings.selectedStaffMember || 0;
      this.selectedYear = settings.selectedYear || this.selectedYear;
      this.selectedMonth = settings.selectedMonth || 0;
      this.activeTab = settings.activeTab || 0;
      this.displayMode = settings.displayMode || "earnings";
      this.showPerStaffMember = !!settings.showPerStaffMember;
    }
  }

  @Watch("selectedStaffMember")
  @Watch("selectedYear")
  @Watch("selectedMonth")
  @Watch("activeTab")
  @Watch("displayMode")
  @Watch("showPerStaffMember")
  onSettingChange() {
    this.saveSettings();
  }

  async created(): Promise<void> {
    this.loadSettings();
    await this.refreshStats();
  }

  async refreshStats() {
    this.loading = true;
    try {
      this.screen = await statisticsModule.getScreen({
        year: this.selectedYear,
        month: this.selectedMonth,
        staffMemberId: this.selectedStaffMember || 0,
      });
    } catch (error) {
      console.error("Error loading statistics screen:", error);
    } finally {
      this.loading = false;
    }
  }
}
</script>
<style lang="scss" scoped>
.content {
  .trend-wrapper {
    margin: 15px 0%;
    width: 98%;
    height: auto;
    background: #fff;
    border-radius: 7px;
    float: left;
    svg {
      height: auto;
    }
    h2 {
      text-align: center;
      margin: 20px 9px;
      font-weight: bold;
    }
  }
}

@media only screen and (max-width: 1264px) {
  .trend-wrapper {
    margin: 6px 1% !important;
  }
}

.header-select {
  font-size: 14px;
}

.header-select ::v-deep .v-input__slot {
  min-height: 36px !important;
  margin-bottom: 0 !important;
}

.v-card {
  .v-card__text {
    padding: 12px 16px;
  }
}

.v-skeleton-loader {
  border-radius: 7px;

  &.mx-auto {
    margin: 0 auto;
  }
}

::v-deep .custom-table {
  .v-data-table-header {
    th {
      background-color: #866afe !important;
      color: white !important;
      font-weight: 600;
    }
  }
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  background-color: #866afe !important;
  color: white !important;
  font-weight: 600;
}

::v-deep .v-radio .v-icon {
  color: #866afe !important;
}

::v-deep .v-radio--is-checked .v-icon {
  color: #866afe !important;
}

::v-deep .v-tabs .v-tabs-slider {
  background: #4a35b5 !important;
}

.horizontal-scroll {
  :deep(.v-data-table__wrapper) {
    overflow-x: auto;
    overflow-y: auto;
    max-height: 500px;

    table {
      min-width: 100%;
      table-layout: auto;
    }

    th,
    td {
      white-space: nowrap;
      min-width: 120px;
    }

    th:first-child,
    td:first-child {
      position: sticky;
      left: 0;
      z-index: 1;
      background: white;
    }

    thead tr th {
      position: sticky;
      top: 0;
      z-index: 1;
      background: #866afe;
    }

    th:first-child {
      z-index: 2;
      background: #866afe;
    }
  }
}

/* ===== Scrollbar CSS ===== */
/* Firefox */
* {
  scrollbar-width: thin;
  scrollbar-color: #866afe #f0f0f0;
}

/* Chrome, Edge, and Safari */
*::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

*::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 4px;
}

*::-webkit-scrollbar-thumb {
  background: linear-gradient(45deg, #866afe, #4a35b5);
  border-radius: 4px;
  border: none;
  transition: background 0.2s ease;
}

*::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(45deg, #4a35b5, #866afe);
}

*::-webkit-scrollbar-corner {
  background: #f0f0f0;
}
</style>
